import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  Box,
  DialogActions,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { Company } from "../../../../../types/companies-managment/company";
import { Country, City } from "../../../../../types/site-params/Country";
import { CompanyActivity } from "../../../../../types/companies-managment/companyActivity";
import { useCountriesForAutocomplete } from "../../../../../hooks/query-hooks/useCountries";
import { useCitiesForAutocomplete } from "../../../../../hooks/query-hooks/useCities";
import { useJobActivitiesForAutocomplete } from "../../../../../hooks/query-hooks/useJobActivities";
import { useDebounce } from "../../../../../hooks";

export type VacancyFiltersType = {
  id: string;
  company_name: string;
  country_id: string;
  city_id: string;
  job_activity_id: string;
};

export type FiltersModalProps = {
  open: boolean;
  handleClose: () => void;
  onApplyFilters: (filters: VacancyFiltersType) => void;
  onClearFilters: () => void;
};

const FiltersModal = ({
  open,
  handleClose,
  onApplyFilters,
}: FiltersModalProps) => {
  const [id, setId] = useState("");
  const [companySearchTerm, setCompanySearchTerm] = useState("");

  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [selectedJobActivity, setSelectedJobActivity] =
    useState<CompanyActivity | null>(null);

  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [jobActivitySearchTerm, setJobActivitySearchTerm] = useState("");

  const debouncedCompanySearchTerm = useDebounce(companySearchTerm, 500);
  const debouncedCountrySearchTerm = useDebounce(countrySearchTerm, 500);
  const debouncedCitySearchTerm = useDebounce(citySearchTerm, 500);
  const debouncedJobActivitySearchTerm = useDebounce(
    jobActivitySearchTerm,
    500
  );

  const {
    items: countries,
    loading: loadingCountries,
    hasMore: hasMoreCountries,
    loadMore: loadMoreCountries,
    error: countriesError,
  } = useCountriesForAutocomplete(20, debouncedCountrySearchTerm);

  const {
    items: cities,
    loading: loadingCities,
    hasMore: hasMoreCities,
    loadMore: loadMoreCities,
    error: citiesError,
  } = useCitiesForAutocomplete(
    selectedCountry?.id || null,
    20,
    debouncedCitySearchTerm
  );

  const {
    items: jobActivities,
    loading: loadingJobActivities,
    hasMore: hasMoreJobActivities,
    loadMore: loadMoreJobActivities,
    error: jobActivitiesError,
  } = useJobActivitiesForAutocomplete(20, debouncedJobActivitySearchTerm);

  useEffect(() => {
    if (!selectedCountry) {
      setSelectedCity(null);
    }
  }, [selectedCountry]);

  const handleApply = () => {
    onApplyFilters({
      id: id,
      company_name: selectedCompany?.name || "",
      country_id: selectedCountry?.id?.toString() || "",
      city_id: selectedCity?.id?.toString() || "",
      job_activity_id: selectedJobActivity?.id?.toString() || "",
    });
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: 500 }}>
        <FormattedMessage id="Filters" />
      </DialogTitle>
      <Divider sx={{ marginInline: "1rem" }} />
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                value={id}
                onChange={(e) => setId(e.target.value)}
                label={<FormattedMessage id="ID" />}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={<FormattedMessage id="Select company" />}
                value={companySearchTerm}
                onChange={(event) => {
                  const value = event.target.value;
                  setCompanySearchTerm(value);
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} marginTop=".5rem">
            <Grid item xs={12} sm={6}>
              <Autocomplete
                value={selectedCountry}
                onChange={(event, newValue) => {
                  setSelectedCountry(newValue);
                  setSelectedCity(null);
                  if (newValue) {
                    setCountrySearchTerm("");
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setCountrySearchTerm(newInputValue);
                  }
                }}
                options={countries}
                getOptionLabel={(option: Country) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={<FormattedMessage id="Country" />}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCountries && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const threshold = 100;
                    if (
                      listboxNode.scrollHeight - listboxNode.scrollTop <=
                        listboxNode.clientHeight + threshold &&
                      hasMoreCountries &&
                      !loadingCountries
                    ) {
                      loadMoreCountries();
                    }
                  },
                }}
                loading={loadingCountries}
                filterOptions={(x) => x} // Disable built-in filtering
                noOptionsText={
                  debouncedCountrySearchTerm.length >= 2
                    ? "No countries found"
                    : "Type to search countries"
                }
              />
              {countriesError && (
                <FormHelperText error>
                  Error loading countries. Please try again.
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                value={selectedCity}
                onChange={(event, newValue) => {
                  setSelectedCity(newValue);
                  // Clear search term when option is selected
                  if (newValue) {
                    setCitySearchTerm("");
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setCitySearchTerm(newInputValue);
                  }
                }}
                options={cities}
                getOptionLabel={(option: City) => option.name}
                disabled={!selectedCountry}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={<FormattedMessage id="City" />}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCities && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const threshold = 100;
                    if (
                      listboxNode.scrollHeight - listboxNode.scrollTop <=
                        listboxNode.clientHeight + threshold &&
                      hasMoreCities &&
                      !loadingCities
                    ) {
                      loadMoreCities();
                    }
                  },
                }}
                loading={loadingCities}
                filterOptions={(x) => x} // Disable built-in filtering
                noOptionsText={
                  debouncedCitySearchTerm.length >= 2
                    ? "No cities found"
                    : "Type to search cities"
                }
              />
              {citiesError && (
                <FormHelperText error>
                  Error loading cities. Please try again.
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Autocomplete
                value={selectedJobActivity}
                onChange={(event, newValue) => {
                  setSelectedJobActivity(newValue);
                  // Clear search term when option is selected
                  if (newValue) {
                    setJobActivitySearchTerm("");
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setJobActivitySearchTerm(newInputValue);
                  }
                }}
                options={jobActivities}
                getOptionLabel={(option: CompanyActivity) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={<FormattedMessage id="Job Activity" />}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingJobActivities && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const threshold = 100;
                    if (
                      listboxNode.scrollHeight - listboxNode.scrollTop <=
                        listboxNode.clientHeight + threshold &&
                      hasMoreJobActivities &&
                      !loadingJobActivities
                    ) {
                      loadMoreJobActivities();
                    }
                  },
                }}
                loading={loadingJobActivities}
                filterOptions={(x) => x} // Disable built-in filtering
                noOptionsText={
                  debouncedJobActivitySearchTerm.length >= 2
                    ? "No job activities found"
                    : "Type to search job activities"
                }
              />
              {jobActivitiesError && (
                <FormHelperText error>
                  Error loading job activities. Please try again.
                </FormHelperText>
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "1rem" }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleClose}
          sx={{ padding: ".3rem 3rem " }}
        >
          <FormattedMessage id="Cancel" />
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          color="primary"
          sx={{ padding: ".3rem 3rem " }}
        >
          <FormattedMessage id="Apply" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiltersModal;
